<template>
    <div ref="root" class="org-multiselect" :class="{ 'org-multiselect--open': open }">
        <span v-if="label" class="org-multiselect-label">{{ label }}</span>
        <button
            type="button"
            class="org-multiselect-toggle form-control form-control-sm"
            :disabled="disabled"
            :aria-expanded="open"
            aria-haspopup="listbox"
            @click="toggle"
        >
            <span class="org-multiselect-summary">{{ summaryText }}</span>
            <i class="material-icons org-multiselect-caret">{{ open ? "expand_less" : "expand_more" }}</i>
        </button>

        <div v-if="open" class="org-multiselect-panel" role="listbox" aria-multiselectable="true">
            <input
                ref="searchInput"
                v-model.trim="search"
                type="search"
                class="form-control form-control-sm"
                placeholder="Search organizations"
                aria-label="Search organizations"
            >
            <div class="org-multiselect-actions">
                <button type="button" class="btn btn-link btn-sm p-0 mb-0" :disabled="!filteredOptions.length" @click="selectVisible">
                    Select {{ search ? "matching" : "all" }}
                </button>
                <button type="button" class="btn btn-link btn-sm p-0 mb-0" :disabled="!modelValue.length" @click="clear">
                    Clear
                </button>
            </div>
            <ul class="org-multiselect-list">
                <li v-for="option in filteredOptions" :key="option.organization_id">
                    <label>
                        <input
                            type="checkbox"
                            :checked="selectedSet.has(option.organization_id)"
                            @change="toggleOption(option.organization_id)"
                        >
                        <span class="org-multiselect-name">{{ option.organization_name }}</span>
                        <small v-if="option.status && option.status !== 'active'" class="org-multiselect-status">{{ option.status }}</small>
                    </label>
                </li>
                <li v-if="!filteredOptions.length" class="org-multiselect-empty">No organizations match your search.</li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps({
    // [{ organization_id, organization_name, status }]
    options: { type: Array, default: () => [] },
    modelValue: { type: Array, default: () => [] },
    label: { type: String, default: "Organizations" },
    allLabel: { type: String, default: "All organizations" },
    disabled: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue"]);

const root = ref(null);
const searchInput = ref(null);
const open = ref(false);
const search = ref("");

const selectedSet = computed(() => new Set(props.modelValue));
const optionById = computed(() => new Map(props.options.map((option) => [option.organization_id, option])));

const filteredOptions = computed(() => {
    const query = search.value.toLowerCase();
    if (!query) return props.options;
    return props.options.filter((option) => String(option.organization_name || "").toLowerCase().includes(query));
});

const summaryText = computed(() => {
    if (!props.modelValue.length) return props.allLabel;
    if (props.modelValue.length === 1) {
        return optionById.value.get(props.modelValue[0])?.organization_name || "1 organization selected";
    }
    return `${props.modelValue.length} organizations selected`;
});

function toggle() {
    open.value = !open.value;
    if (open.value) {
        nextTick(() => searchInput.value?.focus());
    }
}

function toggleOption(id) {
    const next = new Set(props.modelValue);
    if (next.has(id)) {
        next.delete(id);
    } else {
        next.add(id);
    }
    emit("update:modelValue", [...next]);
}

function selectVisible() {
    const next = new Set(props.modelValue);
    filteredOptions.value.forEach((option) => next.add(option.organization_id));
    emit("update:modelValue", [...next]);
}

function clear() {
    emit("update:modelValue", []);
}

function onDocumentClick(event) {
    if (open.value && root.value && !root.value.contains(event.target)) {
        open.value = false;
    }
}

function onKeydown(event) {
    if (event.key === "Escape" && open.value) {
        open.value = false;
    }
}

onMounted(() => {
    document.addEventListener("click", onDocumentClick);
    document.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
    document.removeEventListener("click", onDocumentClick);
    document.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped>
.org-multiselect {
    position: relative;
    display: grid;
    gap: 4px;
    min-width: 240px;
}

.org-multiselect-label {
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 700;
}

.org-multiselect-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    text-align: left;
    cursor: pointer;
}

.org-multiselect-summary {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.org-multiselect-caret {
    flex: 0 0 auto;
    font-size: 18px;
}

.org-multiselect-panel {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 20;
    display: grid;
    gap: 8px;
    width: max(100%, 320px);
    margin-top: 4px;
    padding: 10px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
}

.org-multiselect-actions {
    display: flex;
    justify-content: space-between;
    font-size: 0.74rem;
}

.org-multiselect-list {
    max-height: 240px;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
}

.org-multiselect-list label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 6px;
    border-radius: 6px;
    color: #1e293b;
    font-size: 0.82rem;
    cursor: pointer;
}

.org-multiselect-list label:hover {
    background: #f1f5f9;
}

.org-multiselect-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.org-multiselect-status {
    margin-left: auto;
    padding: 1px 6px;
    border-radius: 999px;
    color: #64748b;
    background: #f1f5f9;
    font-size: 0.66rem;
    text-transform: capitalize;
}

.org-multiselect-empty {
    padding: 8px 6px;
    color: #64748b;
    font-size: 0.8rem;
}
</style>
